/**
 * First-party product analytics (PostHog) consent, kept apart from the
 * advertising consent in trackingManager.ts.
 *
 * Founder decision (Oct 1 2026): our own product analytics counts everyone
 * except people who explicitly opted out. Three gates:
 * - Product analytics: on unless the person opted out on this device (Your
 *   Privacy Choices, the web cookie banner Decline, the Settings toggle), the
 *   browser sends Global Privacy Control, or the signed-in account opted out
 *   (applied to the device at sign-in). An iOS App Tracking Transparency
 *   decline is NOT an opt-out here.
 * - Advertising and attribution: `hasTrackingConsent()` in trackingManager,
 *   unchanged (follows ATT on iOS).
 * - Session replay: only after an explicit Allow (`hasExplicitAnalyticsGrant`).
 *
 * This module reads the same storage keys trackingManager writes, without
 * importing it, so it stays usable from the PostHog module and in tests that
 * mock trackingManager.
 */
import { Capacitor } from '@capacitor/core';

// Storage keys owned by trackingManager.ts (kept in sync by tests).
const SHARED_TRACKING_CONSENT_COOKIE = 'jp_tracking_consent';
const NATIVE_TRACKING_CONSENT_KEY = 'jp_native_tracking_consent';
const NATIVE_APP_TRACKING_OPTOUT_KEY = 'jp_native_app_tracking_opt_out';
/**
 * Set when the member explicitly allows analytics in the app (Your Privacy
 * Choices or the Android toggle). An iOS ATT "Allow" never sets it.
 */
export const NATIVE_ANALYTICS_EXPLICIT_GRANT_KEY = 'jp_native_analytics_explicit_grant';

type ExplicitChoice = 'granted' | 'denied' | null;

function isNativeRuntime(): boolean {
  return Capacitor.isNativePlatform();
}

function isNativeAndroidRuntime(): boolean {
  return isNativeRuntime() && Capacitor.getPlatform() === 'android';
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function hasGlobalPrivacyControl(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

/** The explicit choice stored by the banner, Privacy Choices or Settings; defaults ignored. */
function readStoredExplicitChoice(): ExplicitChoice {
  if (typeof window === 'undefined') return null;

  try {
    const sharedConsent = readCookie(SHARED_TRACKING_CONSENT_COOKIE);
    if (sharedConsent === 'granted' || sharedConsent === 'denied') return sharedConsent;

    const trackingConsent = window.localStorage.getItem('tracking_consent');
    if (trackingConsent === 'granted' || trackingConsent === 'denied') return trackingConsent;

    const cookieConsent = window.localStorage.getItem('cookie_consent_status');
    if (cookieConsent === 'accepted') return 'granted';
    if (cookieConsent === 'declined') return 'denied';
  } catch {
    return null;
  }

  return null;
}

/**
 * An opt-out the person chose on this device: Your Privacy Choices, the web
 * cookie banner Decline, or the Android Settings toggle. Excludes GPC (a
 * browser signal) and an iOS ATT decline. Unreadable native storage counts
 * as opted out.
 */
export function hasExplicitDeviceAnalyticsOptOut(): boolean {
  if (typeof window === 'undefined') return false;

  if (isNativeRuntime()) {
    try {
      if (window.localStorage.getItem(NATIVE_APP_TRACKING_OPTOUT_KEY) === '1') return true;
      if (isNativeAndroidRuntime()) {
        // Android writes 'denied' only from an explicit opt-out (no ATT there).
        if (window.localStorage.getItem(NATIVE_TRACKING_CONSENT_KEY) === 'denied') return true;
        return readStoredExplicitChoice() === 'denied';
      }
      return false;
    } catch {
      return true;
    }
  }

  return readStoredExplicitChoice() === 'denied';
}

/**
 * Product analytics opt-out: an explicit opt-out on this device or a GPC
 * signal. A declined or pending iOS ATT prompt, an EU visitor who has not
 * chosen yet, and a US visitor on the implicit default are not opted out.
 */
export function hasProductAnalyticsOptOut(): boolean {
  return hasGlobalPrivacyControl() || hasExplicitDeviceAnalyticsOptOut();
}

/** PostHog may count this device unless the person opted out. */
export function hasProductAnalyticsConsent(): boolean {
  return !hasProductAnalyticsOptOut();
}

/**
 * True only after the person explicitly allowed analytics: Accept on the web
 * banner, Allow in Your Privacy Choices, or the Settings toggle turned on.
 * Implicit US consent, a pending choice and an iOS ATT "Allow" do not count.
 * Session replay requires this.
 */
export function hasExplicitAnalyticsGrant(): boolean {
  if (hasProductAnalyticsOptOut()) return false;
  if (typeof window === 'undefined') return false;

  if (isNativeRuntime()) {
    try {
      if (window.localStorage.getItem(NATIVE_ANALYTICS_EXPLICIT_GRANT_KEY) === '1') return true;
      // Android builds before the explicit-grant key wrote 'granted' only from
      // the Settings toggle. iOS writes it from ATT, so iOS never counts it.
      return (
        isNativeAndroidRuntime() &&
        window.localStorage.getItem(NATIVE_TRACKING_CONSENT_KEY) === 'granted'
      );
    } catch {
      return false;
    }
  }

  return readStoredExplicitChoice() === 'granted';
}

export function persistNativeAnalyticsExplicitGrant(granted: boolean): void {
  if (typeof window === 'undefined') return;

  try {
    if (granted) {
      window.localStorage.setItem(NATIVE_ANALYTICS_EXPLICIT_GRANT_KEY, '1');
    } else {
      window.localStorage.removeItem(NATIVE_ANALYTICS_EXPLICIT_GRANT_KEY);
    }
  } catch {
    // Storage blocked: replay stays off, which is the safe side.
  }
}

export type ProductAnalyticsStatus = 'denied' | 'granted' | 'pending';

/**
 * Product analytics state, independent of App Tracking Transparency:
 * 'denied' after an opt-out (or GPC), 'granted' after an explicit Allow,
 * otherwise 'pending' (counted, no replay). Your Privacy Choices shows this.
 */
export function getProductAnalyticsStatus(): ProductAnalyticsStatus {
  if (hasProductAnalyticsOptOut()) return 'denied';
  if (hasExplicitAnalyticsGrant()) return 'granted';
  return 'pending';
}

// A choice that could not be saved on the account yet. Retried on app start,
// sign-in, focus and reconnect with backoff; the device choice applies
// meanwhile (an opt-out keeps this device fully opted out).
export const ACCOUNT_ANALYTICS_SYNC_PENDING_KEY = 'jp_analytics_account_sync_pending';

// The account reads and writes (Supabase RPC, retry queue, backoff) live in
// productAnalyticsAccountSync.ts and load on first use, which keeps them out
// of the startup bundle. One shared promise keeps calls in the order made.
type AccountSyncModule = typeof import('@/utils/productAnalyticsAccountSync');
let accountSyncModule: Promise<AccountSyncModule> | null = null;

function loadAccountSync(): Promise<AccountSyncModule> {
  if (!accountSyncModule) {
    accountSyncModule = import('@/utils/productAnalyticsAccountSync').catch((error: unknown) => {
      // Let the next call try again (a flaky network on first load).
      accountSyncModule = null;
      throw error;
    });
  }
  return accountSyncModule;
}

function hasPendingAccountSync(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(ACCOUNT_ANALYTICS_SYNC_PENDING_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Records the analytics choice on the signed-in account
 * (user_privacy_settings.analytics_opt_out, through the
 * set_my_analytics_opt_out RPC) so server-side PostHog captures honor it.
 * Signed-out choices stay on the device. A failed call is kept and retried
 * (see retryPendingAccountAnalyticsSync). Never throws.
 */
export function pushAccountAnalyticsOptOut(optOut: boolean): Promise<boolean> {
  // Bind the choice to whoever is signed in now, before the account code
  // loads, so a sign-in switch during the download cannot move it.
  const ownerId = readStoredAuthUserId();
  if (!ownerId) return Promise.resolve(false);
  return loadAccountSync().then(
    (sync) => sync.pushAccountAnalyticsOptOut(optOut, ownerId),
    () => {
      keepChoiceForRetry(ownerId, optOut);
      return false;
    }
  );
}

/** The signed-in account id from Supabase's stored session (sb-<ref>-auth-token), without loading the client. */
export function readStoredAuthUserId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !/^sb-.+-auth-token$/.test(key)) continue;
      const parsed = JSON.parse(window.localStorage.getItem(key) ?? 'null') as { user?: { id?: unknown } } | null;
      const id = parsed?.user?.id;
      return typeof id === 'string' && id ? id : null;
    }
  } catch {
    // Storage blocked or unreadable: nothing to bind the choice to.
  }
  return null;
}

/**
 * The account code could not load (offline on first use). Keep the choice as
 * a pending sync for the account that made it, so the focus, reconnect
 * and sign-in retries save it once the code loads. Same shape the sync module
 * writes; it retries only for that same account.
 */
function keepChoiceForRetry(userId: string, optOut: boolean): void {
  try {
    window.localStorage.setItem(
      ACCOUNT_ANALYTICS_SYNC_PENDING_KEY,
      JSON.stringify({ userId, optOut, attempts: 0, nextAttemptAt: 0 })
    );
  } catch {
    // Storage blocked: the device choice still applies on this device.
  }
}

/**
 * Retries a choice that could not be saved on the account, for the same
 * signed-in person, once its backoff has passed (`force` skips the wait, for
 * sign-in). Returns true when nothing is left pending.
 */
export function retryPendingAccountAnalyticsSync(options?: { force?: boolean }): Promise<boolean> {
  // Nothing pending (the usual case): no account code is loaded.
  if (!hasPendingAccountSync()) return Promise.resolve(true);
  return loadAccountSync().then(
    (sync) => sync.retryPendingAccountAnalyticsSync(options),
    () => false
  );
}

let accountSyncRetryAttached = false;

/** Retries pending account syncs now and on focus and reconnect. */
export function startAccountAnalyticsSyncRetry(): void {
  void retryPendingAccountAnalyticsSync();
  if (accountSyncRetryAttached || typeof window === 'undefined') return;
  const retry = () => {
    void retryPendingAccountAnalyticsSync();
  };
  window.addEventListener('focus', retry);
  window.addEventListener('online', retry);
  accountSyncRetryAttached = true;
}

export type AccountAnalyticsSyncOutcome =
  | 'applied_to_device'
  | 'pushed_to_account'
  | 'unchanged'
  | 'error';

/**
 * After sign-in: an account-level opt-out is applied to this device through
 * `applyOptOutOnDevice` (the Privacy Choices opt-out, without re-syncing the
 * account), and an explicit opt-out on this device is pushed to the account
 * when the account says otherwise (also retried later if that call fails).
 * A pending choice for this person is retried first. A GPC signal stays per
 * browser and is not pushed. 'error' means the account could not be read
 * (or the account code could not load); callers keep analytics off for this
 * person until a read succeeds.
 */
export async function syncAccountAnalyticsOptOut(
  userId: string,
  applyOptOutOnDevice: () => void
): Promise<AccountAnalyticsSyncOutcome> {
  if (!userId) return 'unchanged';
  try {
    const sync = await loadAccountSync();
    return await sync.syncAccountAnalyticsOptOut(userId, applyOptOutOnDevice);
  } catch {
    return 'error';
  }
}
